'use client'

import { useEffect } from 'react'
import { Button, Cell, Dialog, Navigation, Spacer, Text } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  viewScreenEvent,
  BusinessAccountDto,
} from '@marketplace-web/business-accounts/business-accounts-data'

import useFetchBusinessUserPolicies from './useFetchBusinessUserPolicies'

type Props = {
  business: boolean
  businessAccount?: BusinessAccountDto | null
  show: boolean
  onClose: () => void
}

const PoliciesModal = ({ business, businessAccount, show, onClose }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('user.seller_policies')
  const { policies } = useFetchBusinessUserPolicies(businessAccount?.id)
  const isUKCompanyNumberHidden = useFeatureSwitch('hide_uk_sole_traders_company_number')
  const hideLegalCode =
    isUKCompanyNumberHidden &&
    business &&
    businessAccount?.entity_type === 'sole_trader' &&
    businessAccount?.country === 'UK'

  useEffect(() => {
    if (!show) return

    track(viewScreenEvent({ screen: 'seller_policies' }))
  }, [track, show])

  function renderNavigation() {
    return (
      <Navigation
        body={translate('title')}
        right={<Button styling="flat" onClick={onClose} iconName={X24} />}
      />
    )
  }

  function renderLegalImprint() {
    return (
      <Cell styling="wide">
        <SeparatedList separator={<Spacer size="x2-large" />}>
          <Cell
            styling="tight"
            title={<Text as="h1" type="heading" text={translate('legal_imprint')} />}
            fullWidthTitle
          />
          <Cell
            styling="tight"
            title={translate('business_name')}
            body={
              <Text
                as="span"
                text={businessAccount?.legal_name}
                testId="policies-modal-business-name"
              />
            }
          />
          {!hideLegalCode && (
            <Cell
              styling="tight"
              title={translate('legal_code_number')}
              body={
                <Text
                  as="span"
                  text={businessAccount?.legal_code}
                  testId="policies-modal-legal-code"
                />
              }
            />
          )}
        </SeparatedList>
      </Cell>
    )
  }

  function renderTermsAndConditions() {
    return (
      <Cell
        styling="wide"
        title={<Text as="h1" type="heading" text={translate('terms_and_conditions')} />}
        body={
          <Text
            as="span"
            text={policies?.termsAndConditions || translate('not_specified')}
            testId="policies-modal-terms-conditions"
          />
        }
        fullWidthTitle
      />
    )
  }

  function renderReturnPolicy() {
    return (
      <Cell
        styling="wide"
        title={<Text as="h1" type="heading" text={translate('return_policy')} />}
        body={
          <Text
            as="span"
            text={policies?.returnPolicy || translate('not_specified')}
            testId="policies-modal-return-policy"
          />
        }
        fullWidthTitle
      />
    )
  }

  function renderAdditionalInformation() {
    return (
      <Cell
        styling="wide"
        title={<Text as="h1" type="heading" text={translate('additional_information')} />}
        body={
          <Text
            as="span"
            text={policies?.additionalInformation || translate('not_specified')}
            testId="policies-modal-additional-information"
          />
        }
        fullWidthTitle
      />
    )
  }

  return (
    <Dialog show={show} hasScrollableContent testId="policies-modal">
      <div className="u-fill-width">
        {renderNavigation()}
        <ScrollableArea>
          {renderLegalImprint()}
          {renderTermsAndConditions()}
          {renderReturnPolicy()}
          {renderAdditionalInformation()}
          <Spacer size="large" />
        </ScrollableArea>
      </div>
    </Dialog>
  )
}

export default PoliciesModal
